import {
  Box,
  MenuItem,
  Select,
  IconButton,
  Tooltip,
  Button,
  Theme,
} from "@mui/material";
import { useContext, useCallback, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";

import { LayoutContext } from "../../../contexts/layout/context";
import { DemandWalkEditsContext } from "../../../contexts/demand_walk_edits";
import { Run } from "../../../services/appsync/types";
import { VersionedDataContext } from "../../../contexts/versioned_data/context";
import { useSupplyWalkEdits } from "../../../contexts/supply_walk_edits/provider";
import { useModal } from "../../../contexts/modal/hooks";
import { useForceUserEditConclude } from "../../modals/force_user_edit_conclude";
import dayjs from "../../../utils/dayjs";

export const RunSelectorComponent = (props: {
  loading: boolean;
  mainRun: Run | null;
  setMainRun: (run: Run | null) => void;
  comparedRun: Run | null;
  setComparedRun: (run: Run | null) => void;
  runs: Run[];
}) => {
  const { editedRun } = useContext(VersionedDataContext);
  const { isComparing, setIsComparing } = useContext(LayoutContext);
  const { editMode, discardChanges } = useContext(DemandWalkEditsContext);
  const { openForceUserEditModal } = useForceUserEditConclude();
  const supplyWalkEdits = useSupplyWalkEdits();
  const { openModal, closeModal } = useModal();
  const { loading, mainRun, setMainRun, comparedRun, setComparedRun, runs } =
    props;

  // Store the pending run selection
  const pendingRunRef = useRef<Run | null>(null);

  // Check if there are unsaved changes
  const hasUnsavedChanges =
    editMode ||
    (supplyWalkEdits?.hasUnapprovedEdits &&
      supplyWalkEdits.hasUnapprovedEdits()) ||
    false;

  // Handle run selection with navigation guards
  const handleRunSelection = useCallback(
    (newRun: Run | null) => {
      // Check for unsaved changes dynamically at the time of navigation
      const currentHasUnsavedChanges =
        editMode ||
        (supplyWalkEdits?.hasUnapprovedEdits &&
          supplyWalkEdits.hasUnapprovedEdits()) ||
        false;

      if (currentHasUnsavedChanges) {
        // Store the pending run selection
        pendingRunRef.current = newRun;

        // Use the same modal as the navigation guard
        openModal({
          disableStepViewer: true,
          maxWidth: "sm",
          steps: [
            {
              id: "supply_unsaved_changes",
              title: "Unsaved Changes",
              content:
                "Please approve your changes before navigating away or they will be lost.",
              customActions: (
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                >
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      // Discard changes from both contexts
                      discardChanges();
                      if (supplyWalkEdits?.discardChanges) {
                        supplyWalkEdits.discardChanges();
                      }
                      closeModal();
                      // Now proceed with the run selection
                      setMainRun(pendingRunRef.current);
                      pendingRunRef.current = null;
                    }}
                  >
                    DISCARD CHANGES
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      closeModal();
                    }}
                  >
                    REVIEW CHANGES
                  </Button>
                </Box>
              ),
            },
          ],
        });
      } else {
        // No unsaved changes, proceed directly
        setMainRun(newRun);
      }
    },
    [
      editMode,
      discardChanges,
      supplyWalkEdits,
      openModal,
      closeModal,
      setMainRun,
    ]
  );

  const mainOrLastRun = mainRun || (runs.length > 0 ? runs[0] : null);

  const compareToRuns = runs;

  // Map run to index for MUI Select (value must match MenuItem value which is index)
  const mainRunIndex =
    mainOrLastRun !== null ? runs.indexOf(mainOrLastRun) : -1;
  const comparedRunIndex =
    (comparedRun ?? mainOrLastRun) !== null
      ? compareToRuns.indexOf(comparedRun ?? mainOrLastRun!)
      : -1;

  if (mainOrLastRun === null) {
    return null;
  }

  const toLabelText = (value: unknown): string => {
    if (typeof value === "string") return value.trim();
    if (typeof value === "number") return String(value);
    return "";
  };

  const formatRunLabel = (run: Run | null): string => {
    if (!run) return "Select a run";
    const displayId = toLabelText(run.displayId);
    const name = toLabelText(run.name);
    const ts = dayjs.utc(run.createdAt).local().format("MM/DD/YYYY HH:mm");
    return `${displayId} ${name}`.trim() + ` (${ts})`;
  };

  const pillSx = {
    height: 34,
    backgroundColor: (t: Theme) => t.palette.atomic.panelTint,
    border: (t: Theme) => `1px solid ${t.palette.atomic.border}`,
    borderRadius: "6px",
    "&::before": { borderBottom: "none !important" },
    "&::after": { borderBottom: "none !important" },
    "&:hover:not(.Mui-disabled)::before": {
      borderBottom: "none !important",
    },
    "& .MuiSelect-select": {
      padding: "0 28px 0 10px !important",
      display: "flex",
      alignItems: "center",
      minHeight: "unset !important",
    },
    "& .MuiSelect-icon": { color: (t: Theme) => t.palette.atomic.muted },
  };

  const previewRun = (
    <Select
      value="edits"
      variant="standard"
      size="small"
      disabled={true}
      renderValue={() => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            whiteSpace: "nowrap",
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: (t) => t.palette.atomic.muted,
              flexShrink: 0,
            }}
          />
          <Box
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: (t) => t.palette.atomic.ink,
            }}
          >
            Current edits
          </Box>
        </Box>
      )}
      sx={{ ...pillSx, "& .MuiSelect-icon": { display: "none" } }}
    >
      <MenuItem onClick={() => {}} value="edits">
        current edits
      </MenuItem>
    </Select>
  );

  const mainRunSelector = (
    <Select
      variant="standard"
      size="small"
      value={mainRunIndex >= 0 ? mainRunIndex : 0}
      disabled={loading || runs.length === 0 || Boolean(editedRun)}
      renderValue={() => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            whiteSpace: "nowrap",
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: "#2E9E5B",
              flexShrink: 0,
            }}
          />
          <Box
            component="span"
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: (t) => t.palette.atomic.ink,
            }}
          >
            Run {toLabelText(mainOrLastRun.displayId)}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: 11,
              color: (t) => t.palette.atomic.muted,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            · {dayjs.utc(mainOrLastRun.createdAt).local().format("MM/DD HH:mm")}
          </Box>
        </Box>
      )}
      sx={pillSx}
    >
      {runs.map((run, i) => (
        <MenuItem
          key={i}
          value={i}
          onClick={() => {
            // if it's the topmost item then select null instead
            handleRunSelection(i === 0 ? null : runs[i]);
          }}
        >
          {formatRunLabel(run)}
        </MenuItem>
      ))}
    </Select>
  );

  const comparedRunSelector = (
    <Select
      variant="standard"
      size="small"
      value={comparedRunIndex >= 0 ? comparedRunIndex : 0}
      disabled={loading || runs.length === 0}
      renderValue={() => {
        const displayRun = comparedRun ?? mainOrLastRun;
        if (displayRun === mainOrLastRun && !editedRun) {
          return (
            <Box
              component="span"
              sx={{
                fontSize: 12,
                color: (t) => t.palette.atomic.muted,
                whiteSpace: "nowrap",
              }}
            >
              Select a run
            </Box>
          );
        }
        const name = toLabelText(displayRun.name);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                backgroundColor: "#2A5FDB",
                flexShrink: 0,
              }}
            />
            <Box
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: (t) => t.palette.atomic.ink,
                flexShrink: 0,
              }}
            >
              Run {toLabelText(displayRun.displayId)}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 11,
                color: (t) => t.palette.atomic.muted,
                fontVariantNumeric: "tabular-nums",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              ·{" "}
              {name ||
                dayjs.utc(displayRun.createdAt).local().format("MM/DD HH:mm")}
            </Box>
          </Box>
        );
      }}
      sx={{ ...pillSx, maxWidth: 280 }}
    >
      {compareToRuns.map((run, i) => (
        <MenuItem
          key={i}
          value={i}
          onClick={() => {
            setComparedRun(compareToRuns[i]);
          }}
        >
          {formatRunLabel(run)}
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      {editedRun ? previewRun : mainRunSelector}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "30px",
        }}
      >
        <Tooltip title={isComparing ? "Comparing to" : `Make a comparison`}>
          <IconButton
            onClick={() => {
              if (editMode) {
                openForceUserEditModal();
                return;
              }
              setIsComparing(!isComparing);
            }}
            disabled={loading || runs.length === 0 || isComparing}
          >
            <CompareArrowsIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {isComparing && (
        <>
          {editedRun ? mainRunSelector : comparedRunSelector}
          {!editedRun && (
            <Tooltip title="Clear comparison">
              <IconButton
                onClick={() => {
                  if (editMode) {
                    openForceUserEditModal();
                    return;
                  }
                  setIsComparing(false);
                  setComparedRun(null);
                }}
                disabled={loading || runs.length === 0}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
    </Box>
  );
};
